import React, { useEffect, useMemo, useState } from 'react'
import { color } from '../verificationTheme'
import type { CompositeCell, CompositeSummary } from '../ledger/composite'

// The whole video on one line, with genuine holes.
//
// The strip chart above it shows the last half-minute; this shows everything,
// and its job is to be honest about what has NOT been looked at. A viewer who
// skipped from two minutes to seven leaves a real gap here, and that gap is
// the point: the only claim this product ever makes is "these slices were
// checked", never "the video is fine".
//
// Drawn as RUNS of same-state cells rather than one element per cell. A
// feature film is a thousand cells and would be a thousand DOM nodes redrawn
// every few seconds; the runs are usually a handful, and the picture is
// identical.

const BAR_HEIGHT = 16

interface Run {
  start: number
  end: number
  state: CompositeCell['state']
  /** True when every cell in the run was checked against more than one lane. */
  doubled: boolean
}

export function coverageRuns(cells: CompositeCell[]): Run[] {
  const runs: Run[] = []
  for (const cell of cells) {
    const doubled = cell.by.length > 1
    const last = runs[runs.length - 1]
    if (last && last.state === cell.state && last.doubled === doubled) {
      last.end = cell.end
      continue
    }
    runs.push({ start: cell.start, end: cell.end, state: cell.state, doubled })
  }
  return runs
}

export interface CoverageBarProps {
  summary: CompositeSummary
  /** Ref-backed and stable. Read once a second — a marker on a whole-video
   * bar moves imperceptibly, so this needs no frame loop of its own. */
  getTime: () => number
  frozen?: boolean
}

export default function CoverageBar({ summary, getTime, frozen = false }: CoverageBarProps) {
  const [now, setNow] = useState(() => getTime())

  useEffect(() => {
    if (frozen) return undefined
    const timer = window.setInterval(() => setNow(getTime()), 1000)
    return () => window.clearInterval(timer)
  }, [frozen, getTime])

  const runs = useMemo(() => coverageRuns(summary.cells), [summary])
  const total = summary.cells.length ? summary.cells[summary.cells.length - 1].end : 0
  const pct = (seconds: number) => (total > 0 ? (seconds / total) * 100 : 0)

  return (
    <div
      style={{
        position: 'relative',
        height: BAR_HEIGHT,
        background: color.rule,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {runs.map((run) =>
        run.state === 'unchecked' ? null : (
          <div
            key={`${run.start}-${run.state}`}
            style={{
              position: 'absolute',
              left: `${pct(run.start)}%`,
              width: `${Math.max(pct(run.end - run.start), 0.15)}%`,
              top: 0,
              bottom: 0,
              background: run.state === 'mismatch' ? color.alarm : color.jade,
              boxShadow: run.doubled ? `inset 0 2px 0 ${color.bone}` : undefined,
            }}
          />
        ),
      )}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: `${pct(now)}%`,
          top: -3,
          bottom: -3,
          width: 2,
          marginLeft: -1,
          background: color.bone,
        }}
      />
    </div>
  )
}
